/**
 * Splash de boot — aparece enquanto o backend nao respondeu /api/health.
 * Pollag a cada 400ms; quando responde, esmaece e libera o app.
 *
 * UX:
 *   - Gradiente azul cheio
 *   - Logo SindicoApp grande
 *   - Spinner suave
 *   - "Dica" rotativa do que esta acontecendo
 *   - Footer CanadaTechBR
 */
import { useEffect, useState } from "react";
import { Building2 } from "lucide-react";

interface Props { onPronto: () => void }

const DICAS = [
  "Conectando ao banco...",
  "Carregando catálogos...",
  "Preparando seus relatórios...",
  "Quase lá...",
];

export function SplashScreen({ onPronto }: Props) {
  const [dicaIdx, setDicaIdx] = useState(0);

  // Rotaciona as dicas a cada 1.4s
  useEffect(() => {
    const t = setInterval(() => setDicaIdx((i) => (i + 1) % DICAS.length), 1400);
    return () => clearInterval(t);
  }, []);

  // Polling do backend a cada 400ms
  useEffect(() => {
    let cancelado = false;
    const tentar = async () => {
      while (!cancelado) {
        try {
          const r = await fetch("/api/health", { cache: "no-store" });
          if (r.ok) {
            onPronto();
            return;
          }
        } catch {
          // backend ainda subindo
        }
        await new Promise((res) => setTimeout(res, 400));
      }
    };
    tentar();
    return () => { cancelado = true; };
  }, [onPronto]);

  return (
    <div
      className="fixed inset-0 flex flex-col items-center justify-center text-white z-50"
      style={{
        background:
          "radial-gradient(1000px 700px at 50% -12%, rgba(34,201,184,0.18), transparent 60%)," +
          "radial-gradient(720px 620px at 12% 120%, rgba(60,110,190,0.35), transparent 60%)," +
          "linear-gradient(160deg,#0a1322 0%,#0d1a2e 55%,#0f2140 100%)",
      }}
    >
      <div className="flex flex-col items-center gap-6 px-6">
        <div
          className="relative w-24 h-24 rounded-2xl flex items-center justify-center"
          style={{
            background:
              "radial-gradient(130% 90% at 50% -15%, rgba(34,201,184,0.35), transparent 55%)," +
              "linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.03))",
            border: "1px solid rgba(255,255,255,0.14)",
            boxShadow: "0 20px 50px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.10)",
          }}
        >
          <span
            aria-hidden
            className="absolute inset-x-0 top-0 h-[3px] rounded-t-2xl"
            style={{ background: "#22C9B8", boxShadow: "0 0 10px 1px rgba(34,201,184,0.9), 0 0 22px 3px rgba(34,201,184,0.5)" }}
          />
          <Building2 size={44} style={{ color: "#22C9B8" }} />
        </div>
        <div className="text-center">
          <h1 className="text-4xl font-semibold tracking-tight">
            Síndico<span style={{ color: "#22C9B8" }}>App</span>
          </h1>
          <p className="text-slate-300/80 text-sm mt-1.5">Gestão condominial inteligente</p>
        </div>
        <div className="flex items-center gap-3 mt-2">
          <div className="relative h-1.5 w-52 bg-white/10 rounded-full overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 w-1/3 rounded-full animate-[slide_1.5s_ease-in-out_infinite]"
              style={{ background: "linear-gradient(90deg,#22C9B8,#0E8D82)", boxShadow: "0 0 10px rgba(34,201,184,0.7)" }}
            />
          </div>
        </div>
        <p className="text-sm text-slate-300/90 transition-opacity duration-300 min-h-[1.25rem]">
          {DICAS[dicaIdx]}
        </p>
      </div>
      <div className="absolute bottom-6 text-xs text-slate-400/70">
        CanadaTechBR · SíndicoApp
      </div>
      <style>{`
        @keyframes slide {
          0%   { transform: translateX(-100%); }
          50%  { transform: translateX(100%);  }
          100% { transform: translateX(200%);  }
        }
      `}</style>
    </div>
  );
}
